/**
 * Truth v2 profitability client (Phase 4).
 * Fetches from zeecore /profitability/v2/* endpoints.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import type {
  ProfitabilityAssumptionsConfig,
  TruthComputeJobRecord,
  TruthComputeScope,
  TruthCoverageReport,
  TruthCustomerMonthlyRow,
  TruthCustomerDailyRow,
  TruthAwaitingMergedOrderSummary,
  TruthNetworkCostSummary,
  TruthLastComputedInfo,
  TruthMergedCostRow,
  TruthMergedOrderRow,
  TruthStatusResponse,
  TruthTelematicsDeliveryStop,
  TruthTelematicsRouteSegment,
} from '../types/profitabilityTruth';

export type {
  ProfitabilityAssumptionsConfig,
  TruthMergedCostRow,
  TruthMergedOrderRow,
  TruthCustomerMonthlyRow,
  TruthCustomerDailyRow,
  TruthStatusResponse,
  TruthComputeJobRecord,
  TruthCoverageReport,
  TruthTelematicsDeliveryStop,
  TruthTelematicsRouteSegment,
};

type TruthFetchOptions = {
  /** Bust browser/CDN caches after compute completes (monotonic counter). */
  cacheBust?: number;
  signal?: AbortSignal;
};

function withCacheBust(url: string, cacheBust?: number): string {
  if (cacheBust == null) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_=${cacheBust}`;
}

const TRUTH_GET_INIT = (opts?: TruthFetchOptions): RequestInit => ({
  cache: 'no-store',
  signal: opts?.signal,
});

export interface RevenueMonthUsed {
  basis: 'xero_monthly_final' | 'xero_monthly_provisional' | 'xero_carried_forward';
  outbound_per_job_gbp: number | null;
  inbound_per_job_gbp: number | null;
}

/** Read-time Assumed £/job meta from zeecore GET responses (null when nothing filled). */
export type TruthRevenueImputationMeta = {
  kind: 'assumed_avg_per_job';
  /** The single average: only for jobs with no monthly figure when `months_used` is present. */
  avg_gbp_per_job: number;
  /** Months whose Xero figures filled jobs (plan step 3). */
  months_used?: Record<string, RevenueMonthUsed>;
  /** Jobs filled at the single average because no monthly figure applied. */
  filled_at_single_average?: number;
  reference_period: { start: string; end: string };
  provenance: {
    customer_revenue_gbp: number;
    job_count: number;
    job_count_source: string;
    computed_at: string;
  };
  vehicle_days_filled: number;
  orders_filled: number;
  customers_filled: number;
};

export type TruthMergedCostsResult = {
  records: TruthMergedCostRow[];
  revenue_imputation: TruthRevenueImputationMeta | null;
};

/**
 * Fetch truth v2 merged costs (equivalent to batch merged_costs.csv).
 * Includes `revenue_imputation` when Assumed £/job filled any vehicle-days.
 */
export async function fetchTruthMergedCosts(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthMergedCostsResult> {
  const url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.mergedCosts(startDate, endDate),
    options?.cacheBust,
  );
  const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth merged-costs failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  return {
    records: json.records || [],
    revenue_imputation: json.revenue_imputation ?? null,
  };
}

export type TruthNetworkRevenueImputation = {
  enabled: boolean;
  avg_gbp_per_job: number;
  reference_period: { start: string; end: string };
  provenance: {
    customer_revenue_gbp: number;
    job_count: number;
    job_count_source: string;
    computed_at: string;
  };
};

export type TruthXeroProfitKpisResponse = {
  available: boolean;
  kpiMode: 'xero_books' | null;
  reason?: string;
  startDate: string;
  endDate: string;
  kpis: {
    netProfitGbp: number;
    marginPct: number;
    retainedRevenueGbp: number;
    customerRevenueGbp: number;
    networkChargesGbp: number;
    ownCostsGbp: number;
    fuelGbp: number;
    driversGbp: number;
    standingGbp: number;
    hireOfMotorVehicleGbp?: number | null;
    motorExpensesGbp?: number | null;
    agencyStaffHireGbp?: number | null;
    workingDays: number;
    stampLabel: string;
  } | null;
  provenance?: {
    sources: string[];
    books_source?: 'profit_and_loss' | 'invoice_rollup' | null;
    standing_source?: string | null;
    partial_revenue_mapping?: boolean;
    pnl_from?: string;
    pnl_to?: string;
  };
  gaps?: {
    unmapped_account_codes?: string[];
    unallocated_purchase_credit_gbp?: number;
  };
  /** Declared Assumed £/job from profitability assumptions (may be disabled). */
  network_revenue_imputation?: TruthNetworkRevenueImputation | null;
};

export type TruthProfitKpisFetchResult =
  | { status: 'ok'; data: TruthXeroProfitKpisResponse }
  | { status: 'http_error'; httpStatus: number }
  | { status: 'network_error' };

/**
 * Company KPIs from ingested Xero Accounting (Phase 2).
 * Distinguishes unreachable API from `available: false` (no books in window).
 */
export async function fetchTruthProfitKpis(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthProfitKpisFetchResult> {
  let url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.profitKpis(startDate, endDate),
    options?.cacheBust,
  );
  // Bypass server derived KPI snapshot after Refresh / explicit bust.
  if (options?.cacheBust != null) {
    url += `${url.includes('?') ? '&' : '?'}fresh=true`;
  }
  try {
    const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
    if (!response.ok) {
      return { status: 'http_error', httpStatus: response.status };
    }
    const data = (await response.json()) as TruthXeroProfitKpisResponse;
    return { status: 'ok', data };
  } catch (err) {
    // Propagate abort so period switches do not paint a false network_error.
    if (
      (err instanceof DOMException && err.name === 'AbortError')
      || (err instanceof Error && err.name === 'AbortError')
    ) {
      throw err;
    }
    return { status: 'network_error' };
  }
}

/**
 * Fetch truth v2 merged orders (equivalent to batch merged_orders.csv).
 */
export type TruthMergedOrdersResult = {
  rows: TruthMergedOrderRow[];
  revenue_imputation: TruthRevenueImputationMeta | null;
  network_cost: TruthNetworkCostSummary | null;
  /** zeecore's postcode geocoder was still loading: missing coords may fill on a later request. */
  coordsPending: boolean;
};

export async function fetchTruthMergedOrders(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthMergedOrdersResult> {
  const url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.mergedOrders(startDate, endDate),
    options?.cacheBust,
  );
  const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth merged-orders failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  return {
    rows: json.rows || [],
    revenue_imputation: json.revenue_imputation ?? null,
    network_cost: json.network_cost ?? null,
    coordsPending: json.coords_pending === true,
  };
}

/**
 * Fetch truth v2 customer monthly profitability (Step 4 shape).
 */
export async function fetchTruthCustomerProfitabilityMonthly(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthCustomerMonthlyRow[]> {
  const url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.customerMonthly(startDate, endDate),
    options?.cacheBust,
  );
  const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth customer-profitability-monthly failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  return json.rows || [];
}

/**
 * Fetch truth v2 customer daily profitability (Step 4 daily shape).
 */
export type TruthCustomerDailyResult = {
  rows: TruthCustomerDailyRow[];
  revenue_imputation: TruthRevenueImputationMeta | null;
  awaiting_merged_order: TruthAwaitingMergedOrderSummary | null;
};

export async function fetchTruthCustomerProfitabilityDaily(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthCustomerDailyResult> {
  const url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.customerDaily(startDate, endDate),
    options?.cacheBust,
  );
  const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth customer-profitability-daily failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  return {
    rows: json.rows || [],
    revenue_imputation: json.revenue_imputation ?? null,
    awaiting_merged_order: json.awaiting_merged_order ?? null,
  };
}

/**
 * Pipeline status for a date range (sources, warnings, last compute, optional coverage).
 */
export async function fetchTruthStatus(
  startDate: string,
  endDate: string,
  options?: { includeCoverage?: boolean; probeSources?: boolean; signal?: AbortSignal },
): Promise<TruthStatusResponse> {
  const url = API_ENDPOINTS.profitabilityV2.status(startDate, endDate, undefined, {
    includeCoverage: options?.includeCoverage,
    probeSources: options?.probeSources,
  });
  const response = await authenticatedFetch(url, {
    cache: 'no-store',
    signal: options?.signal,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth status failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return await response.json();
}

/** Months with stored vehicle-day profit, newest first (Custom period picker). */
export async function fetchTruthProfitMonths(
  options?: { signal?: AbortSignal },
): Promise<Array<{ month: string; days: number }>> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.months(), {
    signal: options?.signal,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Profit months failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const body = (await response.json()) as { months?: Array<{ month: string; days: number }> };
  return Array.isArray(body.months) ? body.months : [];
}

export async function fetchTruthCoverage(
  startDate: string,
  endDate: string,
): Promise<TruthCoverageReport> {
  const url = API_ENDPOINTS.profitabilityV2.coverage(startDate, endDate);
  const response = await authenticatedFetch(url, { cache: 'no-store' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth coverage failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return await response.json();
}

export async function fetchTruthComputeJobs(): Promise<TruthComputeJobRecord[]> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.jobsList(), {
    cache: 'no-store',
  });
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.jobs) ? data.jobs : [];
}

export async function triggerTruthCompute(
  startDate: string,
  endDate: string,
  scope: TruthComputeScope = 'all',
): Promise<void> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.compute(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startDate, endDate, scope }),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(err.message ?? `Compute request failed (${response.status})`);
  }
}

export async function cancelTruthComputeJob(jobId: string): Promise<void> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.cancelJob(jobId), {
    method: 'POST',
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(err.error ?? err.message ?? `Cancel failed (${response.status})`);
  }
}

/** Normalise manifest / status last_computed shapes. */
export function truthLastComputedMeta(last: TruthLastComputedInfo | null | undefined): {
  computedAt: string | null;
  recordCount: number | null;
  startDate: string | null;
  endDate: string | null;
} {
  if (!last) {
    return { computedAt: null, recordCount: null, startDate: null, endDate: null };
  }
  const computedAt = last.computed_at ?? last.computedAt ?? null;
  const recordCount = last.record_count ?? last.recordCount ?? null;
  const startDate = last.start_date ?? last.startDate ?? null;
  const endDate = last.end_date ?? last.endDate ?? null;
  return { computedAt, recordCount, startDate, endDate };
}

/**
 * Check if truth data is available for the given window.
 */
export async function isTruthDataAvailable(
  startDate: string,
  endDate: string,
): Promise<boolean> {
  try {
    const status = await fetchTruthStatus(startDate, endDate, { probeSources: false });
    const { recordCount } = truthLastComputedMeta(status.last_computed);
    return (recordCount ?? 0) > 0;
  } catch {
    return false;
  }
}

/** Load declared profitability assumptions (zeecore canonical config). */
export async function fetchTruthProfitabilityConfig(): Promise<ProfitabilityAssumptionsConfig | null> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.config(), {
    cache: 'no-store',
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth profitability config failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as ProfitabilityAssumptionsConfig;
}

/** One month of the network charge per outbound consignment: worked out from Xero, and set by hand. */
export interface NetworkCostMonthRow {
  month: string;
  computed: {
    per_outbound_gbp: number | null;
    palletways_charges_gbp: number | null;
    outbound_consignments: number | null;
    status: 'provisional' | 'final';
    flagged_big_move?: true;
    compared_with?: { month: string; per_outbound_gbp: number };
    missing?: string;
    computed_at: string;
  } | null;
  versions: number;
  hand_set_per_outbound_gbp: number | null;
  /** Average revenue per job by direction from the P&L income lines (shown only; plan step 3). */
  revenue?: {
    outbound_per_job_gbp: number | null;
    inbound_per_job_gbp: number | null;
    direct_customers_gbp: number | null;
    direct_income_gbp: number | null;
    outbound_consignments: number | null;
    inbound_consignments: number | null;
    local_consignments: number | null;
    status: 'provisional' | 'final';
    /** A direction that moved more than 20% on the previous final month: shown, not used. */
    flagged_big_move?: { outbound?: true; inbound?: true };
    compared_with?: { month: string; outbound_per_job_gbp: number | null; inbound_per_job_gbp: number | null };
  } | null;
}

export interface NetworkCostMonthlyRates {
  months: NetworkCostMonthRow[];
  /** The single £/job profit fills jobs with today. */
  revenue_in_use: { avg_gbp_per_job: number; monthly_from_xero?: boolean } | null;
}

/** The last six months of network charge per outbound consignment (plan: xero_monthly_rates_plan.md, step 1). */
export async function fetchNetworkCostMonthlyRates(): Promise<NetworkCostMonthlyRates> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.networkCostMonthlyRates(), { cache: 'no-store' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Network charge months failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as NetworkCostMonthlyRates;
}

export type CostCheckHeadingKey = 'fuel' | 'driver_pay' | 'vehicle' | 'network';

/** One month of our modelled costs against the Xero P&L (plan: xero_monthly_rates_plan.md, step 5a). */
export interface CostCheckMonth {
  month: string;
  status: 'provisional' | 'final';
  missing?: string;
  headings: Array<{
    heading: CostCheckHeadingKey;
    modelled_gbp: number | null;
    xero_gbp: number | null;
    gap_gbp: number | null;
    gap_pct: number | null;
    xero_lines: Array<{ label: string; amount: number }>;
  }>;
  unmapped_cost_of_sales: Array<{ label: string; amount: number }>;
  operating_expenses_gbp: number | null;
  coverage: { days_in_month: number; days_with_profit: number; vehicle_days: number; no_gps_vehicle_days: number };
  inputs: { pnl_raw_payload_hash: string | null; mapping_version: string; mapping_confirmed: false };
}

export async function fetchCostCheckMonthly(): Promise<{ months: CostCheckMonth[] }> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.costCheckMonthly(), { cache: 'no-store' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Cost check failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as { months: CostCheckMonth[] };
}

/** Built-in server profitability defaults — what "Reset" restores. */
export async function fetchTruthProfitabilityConfigDefaults(): Promise<ProfitabilityAssumptionsConfig> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.configDefaults(), {
    cache: 'no-store',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth profitability defaults failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as ProfitabilityAssumptionsConfig;
}

/** Save profitability assumptions; returns persisted config from zeecore. */
export async function saveTruthProfitabilityConfig(
  config: ProfitabilityAssumptionsConfig,
  options?: { saveReason?: 'reset_to_defaults' },
): Promise<ProfitabilityAssumptionsConfig> {
  const response = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.config(), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...config,
      ...(options?.saveReason ? { save_reason: options.saveReason } : {}),
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Save profitability config failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as ProfitabilityAssumptionsConfig;
}

/** Normalise a truth/legacy delivery-stop row for MapPin + Vehicle Map. */
export function normalizeTruthDeliveryStop(raw: Record<string, unknown>): TruthTelematicsDeliveryStop {
  const assetName = String(raw.AssetName ?? raw.vehicle ?? raw.Vehicle ?? '').trim();
  const dayDate = String(raw.day_date ?? raw.date ?? raw.Date ?? '').trim().slice(0, 10);
  return {
    AssetName: assetName,
    day_date: dayDate,
    stop_number: Number(raw.stop_number ?? raw.stopNumber ?? 0) || 0,
    latitude: Number(raw.latitude ?? raw.lat ?? 0) || 0,
    longitude: Number(raw.longitude ?? raw.lon ?? raw.lng ?? 0) || 0,
    duration_minutes: Number(raw.duration_minutes ?? raw.durationMinutes ?? 0) || 0,
    miles_to_next_stop: Number(raw.miles_to_next_stop ?? raw.milesToNextStop ?? 0) || 0,
    arrival_time: String(raw.arrival_time ?? raw.arrivalTime ?? ''),
    departure_time: String(raw.departure_time ?? raw.departureTime ?? ''),
    postcode: String(raw.postcode ?? ''),
    stop_type: String(raw.stop_type ?? raw.stopType ?? ''),
  };
}

/** Normalise a truth/legacy route-segment row for Vehicle Map polylines. */
export function normalizeTruthRouteSegment(raw: Record<string, unknown>): TruthTelematicsRouteSegment {
  const vehicle = String(raw.vehicle ?? raw.AssetName ?? raw.Vehicle ?? '').trim();
  const date = String(raw.date ?? raw.day_date ?? raw.Date ?? '').trim().slice(0, 10);
  const pointsRaw = raw.points;
  const points = Array.isArray(pointsRaw)
    ? pointsRaw
      .map((p) => {
        if (Array.isArray(p) && p.length >= 2) {
          return [Number(p[0]), Number(p[1])] as [number, number];
        }
        if (p && typeof p === 'object') {
          const obj = p as { lat?: unknown; lon?: unknown; lng?: unknown };
          const lat = Number(obj.lat);
          const lon = Number(obj.lon ?? obj.lng);
          if (Number.isFinite(lat) && Number.isFinite(lon)) return [lat, lon] as [number, number];
        }
        return null;
      })
      .filter((p): p is [number, number] => p != null)
    : [];
  return {
    vehicle,
    date,
    from_stop_number: Number(raw.from_stop_number ?? raw.fromStopNumber ?? 0) || 0,
    to_stop_number: Number(raw.to_stop_number ?? raw.toStopNumber ?? 0) || 0,
    segment_start: raw.segment_start != null ? String(raw.segment_start) : undefined,
    segment_end: raw.segment_end != null ? String(raw.segment_end) : undefined,
    from_stop: raw.from_stop as TruthTelematicsRouteSegment['from_stop'],
    to_stop: raw.to_stop as TruthTelematicsRouteSegment['to_stop'],
    n_points_raw: raw.n_points_raw != null ? Number(raw.n_points_raw) : undefined,
    n_points_kept: raw.n_points_kept != null ? Number(raw.n_points_kept) : undefined,
    points,
  };
}

/**
 * Fetch truth v2 telematics delivery stops (MapPin / Vehicle Map STOPS).
 */
export async function fetchTruthTelematicsDeliveryStops(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthTelematicsDeliveryStop[]> {
  const url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.telematicsStops(startDate, endDate),
    options?.cacheBust,
  );
  const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth telematics-delivery-stops failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  const rows = Array.isArray(json.delivery_stops)
    ? json.delivery_stops
    : Array.isArray(json.records)
      ? json.records
      : [];
  return rows
    .filter((r: unknown): r is Record<string, unknown> => r != null && typeof r === 'object')
    .map((r: Record<string, unknown>) => normalizeTruthDeliveryStop(r));
}

/**
 * Fetch truth v2 telematics route segments (Vehicle Map blue Seg polylines).
 */
export async function fetchTruthTelematicsRouteSegments(
  startDate: string,
  endDate: string,
  options?: TruthFetchOptions,
): Promise<TruthTelematicsRouteSegment[]> {
  const url = withCacheBust(
    API_ENDPOINTS.profitabilityV2.telematicsSegments(startDate, endDate),
    options?.cacheBust,
  );
  const response = await authenticatedFetch(url, TRUTH_GET_INIT(options));
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Truth telematics-route-segments failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  const rows = Array.isArray(json.route_segments)
    ? json.route_segments
    : Array.isArray(json.records)
      ? json.records
      : [];
  return rows
    .filter((r: unknown): r is Record<string, unknown> => r != null && typeof r === 'object')
    .map((r: Record<string, unknown>) => normalizeTruthRouteSegment(r));
}
